import React, { useEffect, useState } from "react";
import AdminSidebar from "../components/AdminSidebar";
import api from "../lib/api";
import { toast } from "sonner";
import { Star, MessageSquare, Reply, Trash2, Send, X } from "lucide-react";

export default function AdminReviews() {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [replying, setReplying] = useState(null);
    const [replyText, setReplyText] = useState("");
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/admin/reviews");
            setList(r.data);
        } catch {
            toast.error("Errore caricamento");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const openReply = (rev) => {
        setReplying(rev.review_id);
        setReplyText(rev.owner_reply || "");
    };

    const submitReply = async () => {
        if (!replyText.trim()) { toast.error("Risposta vuota"); return; }
        setSaving(true);
        try {
            await api.post(`/admin/reviews/${replying}/reply`, { text: replyText });
            toast.success("Risposta pubblicata");
            setReplying(null);
            setReplyText("");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        } finally {
            setSaving(false);
        }
    };

    const deleteReply = async (review_id) => {
        if (!window.confirm("Rimuovere la risposta pubblica?")) return;
        try {
            await api.delete(`/admin/reviews/${review_id}/reply`);
            toast.success("Risposta rimossa");
            load();
        } catch {
            toast.error("Errore");
        }
    };

    const avg = list.length ? (list.reduce((s, r) => s + r.stars, 0) / list.length).toFixed(1) : "—";

    return (
        <div className="flex min-h-screen bg-ink" data-testid="admin-reviews-page">
            <AdminSidebar />
            <main className="flex-1 p-10">
                <div className="mb-8">
                    <div className="text-gold text-xs tracking-[0.4em] uppercase">Gestione</div>
                    <h1 className="font-serif text-5xl text-white mt-2">Recensioni</h1>
                    <p className="text-gray-400 text-sm mt-2">Rispondi pubblicamente alle recensioni dei clienti. La tua replica appare sotto la recensione nella pagina del ristorante.</p>
                </div>

                <div className="grid sm:grid-cols-3 gap-4 mb-10">
                    <Stat label="Totali" value={list.length} />
                    <Stat label="Media stelle" value={avg} gold />
                    <Stat label="Senza risposta" value={list.filter((r) => !r.owner_reply).length} />
                </div>

                {loading ? (
                    <div className="text-gray-500">Caricamento...</div>
                ) : list.length === 0 ? (
                    <div className="bg-ink-surface border border-white/10 rounded-sm p-12 text-center text-gray-500">
                        Nessuna recensione ancora.
                    </div>
                ) : (
                    <div className="space-y-4">
                        {list.map((rev) => (
                            <div key={rev.review_id} className="bg-ink-surface border border-white/10 rounded-sm p-6" data-testid={`admin-review-${rev.review_id}`}>
                                <div className="flex items-start justify-between flex-wrap gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="flex gap-0.5">
                                                {[1, 2, 3, 4, 5].map((n) => (
                                                    <Star key={n} size={14}
                                                        className={n <= rev.stars ? "text-gold fill-gold" : "text-white/15"}
                                                        strokeWidth={1.5} />
                                                ))}
                                            </div>
                                            <span className="text-sm text-white font-semibold">{rev.user_name}</span>
                                            <span className="text-[10px] uppercase tracking-widest text-gray-500">
                                                {(rev.created_at || "").slice(0, 10)}
                                            </span>
                                        </div>
                                        {rev.text && <p className="text-sm text-gray-300 leading-relaxed italic">"{rev.text}"</p>}

                                        {rev.owner_reply && (
                                            <div className="mt-4 pl-4 border-l-2 border-gold/40 bg-gold/5 rounded-r-sm p-4" data-testid={`reply-${rev.review_id}`}>
                                                <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-1">La tua risposta</div>
                                                <p className="text-sm text-gray-200 leading-relaxed">{rev.owner_reply}</p>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={() => openReply(rev)}
                                            className="inline-flex items-center gap-1.5 text-xs text-gold border border-gold/40 px-3 py-2 rounded-sm hover:bg-gold/10 uppercase tracking-widest"
                                            data-testid={`reply-btn-${rev.review_id}`}>
                                            <Reply size={12} /> {rev.owner_reply ? "Modifica" : "Rispondi"}
                                        </button>
                                        {rev.owner_reply && (
                                            <button onClick={() => deleteReply(rev.review_id)}
                                                className="p-2 text-gray-500 hover:text-red-400"
                                                data-testid={`delete-reply-${rev.review_id}`}>
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>

            {replying && (
                <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-4" data-testid="reply-modal">
                    <div className="bg-ink-surface border border-gold/30 rounded-sm p-8 max-w-lg w-full space-y-5">
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="text-gold text-xs tracking-[0.4em] uppercase mb-1">Rispondi alla recensione</div>
                                <h2 className="font-serif text-2xl text-white">La tua replica pubblica</h2>
                            </div>
                            <button onClick={() => setReplying(null)} className="text-gray-400 hover:text-white" data-testid="close-reply-modal">
                                <X size={18} />
                            </button>
                        </div>
                        <div className="relative">
                            <MessageSquare className="absolute left-3 top-3 text-gray-500" size={16} strokeWidth={1.5} />
                            <textarea rows={5} value={replyText} onChange={(e) => setReplyText(e.target.value)}
                                placeholder="Ringrazia, spiega, invita a tornare…"
                                data-testid="reply-text-input"
                                className="w-full pl-10 pr-3 py-3 bg-transparent border border-white/20 rounded-sm text-white placeholder-gray-500 focus:border-gold focus:outline-none text-sm resize-none" />
                        </div>
                        <p className="text-[11px] text-gray-500">La risposta è pubblica e visibile a tutti i clienti nella pagina del ristorante.</p>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setReplying(null)} disabled={saving}
                                className="text-xs text-gray-400 hover:text-white px-4 py-2.5 uppercase tracking-widest">
                                Annulla
                            </button>
                            <button onClick={submitReply} disabled={saving || !replyText.trim()}
                                className="bg-gold text-black font-semibold px-6 py-2.5 rounded-sm hover:bg-gold-light tracking-widest uppercase text-xs disabled:opacity-50 inline-flex items-center gap-2"
                                data-testid="reply-submit-btn">
                                <Send size={12} /> {saving ? "Invio..." : "Pubblica"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, gold }) {
    return (
        <div className="bg-ink-surface border border-white/10 rounded-sm p-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-gray-400">{label}</div>
            <div className={`font-serif text-4xl mt-2 ${gold ? "text-gold" : "text-white"}`}>{value}</div>
        </div>
    );
}
